import { isCpf } from 'validator-brazil';

export const CNPJ_MASK = 'AA.AAA.AAA/AAAA-00';
export const CPF_MASK = '000.000.000-00';
export const CPF_CNPJ_MASK = `${CNPJ_MASK}||${CPF_MASK}`;

const CNPJ_FIRST_DV_WEIGHTS = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
const CNPJ_SECOND_DV_WEIGHTS = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

export function cleanDocument(value: unknown): string {
  if (value == null || value === '') {
    return '';
  }

  return String(value)
    .replace(/[.\-/\s]/g, '')
    .toUpperCase();
}

export function hasLetter(value: unknown): boolean {
  return /[a-zA-Z]/.test(String(value ?? ''));
}

export function resolveCpfCnpjMask(value: unknown): string {
  if (hasLetter(value)) {
    return CNPJ_MASK;
  }

  const clean = cleanDocument(value);
  if (clean.length > 11) {
    return CNPJ_MASK;
  }

  return CPF_CNPJ_MASK;
}

export function documentInputTransform(
  value: string | number | null | undefined,
): string | number | null | undefined {
  if (typeof value !== 'string' || !/[a-z]/.test(value)) {
    return value;
  }

  return value.toUpperCase();
}

function getCnpjCharValue(char: string): number {
  return char.charCodeAt(0) - 48;
}

function calculateCnpjCheckDigit(base: string, weights: number[]): number {
  const sum = base
    .split('')
    .reduce((total, char, index) => total + getCnpjCharValue(char) * weights[index], 0);

  const remainder = sum % 11;
  return remainder < 2 ? 0 : 11 - remainder;
}

export function isValidCnpj(value: unknown): boolean {
  const doc = cleanDocument(value);

  if (doc.length !== 14) {
    return false;
  }

  if (!/^[A-Z0-9]{12}\d{2}$/.test(doc)) {
    return false;
  }

  if (/^(.)\1+$/.test(doc)) {
    return false;
  }

  const firstDigit = calculateCnpjCheckDigit(doc.slice(0, 12), CNPJ_FIRST_DV_WEIGHTS);
  if (firstDigit !== Number(doc.charAt(12))) {
    return false;
  }

  const secondDigit = calculateCnpjCheckDigit(doc.slice(0, 13), CNPJ_SECOND_DV_WEIGHTS);
  return secondDigit === Number(doc.charAt(13));
}

export function isValidCpf(value: unknown): boolean {
  const doc = cleanDocument(value);

  if (!doc || hasLetter(doc)) {
    return false;
  }

  return isCpf(doc);
}

export function isValidCpfCnpj(value: unknown): boolean {
  const doc = cleanDocument(value);

  if (!doc) {
    return false;
  }

  if (hasLetter(doc) || doc.length > 11) {
    return isValidCnpj(doc);
  }

  return isCpf(doc);
}

export function formatCpfCnpj(value: unknown): string {
  const doc = cleanDocument(value);

  if (!doc) {
    return '';
  }

  if (doc.length <= 11) {
    const cpf = doc.padStart(11, '0');
    if (!/^\d{11}$/.test(cpf)) {
      return doc;
    }

    return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }

  const cnpj = doc.padStart(14, '0').slice(-14);
  return cnpj.replace(/^(.{2})(.{3})(.{3})(.{4})(.{2})$/, '$1.$2.$3/$4-$5');
}

export function normalizeDocumentForApi(value: unknown): string {
  return cleanDocument(value);
}

export function documentToFilterValue(value: unknown): string | null {
  const doc = cleanDocument(value);
  return doc || null;
}

//Lê CPF/CNPJ de query param sem JSON.parse (quebra CNPJ alfanumérico)
export function parseDocumentFromQueryParam(value: unknown): string | undefined {
  if (value == null || value === '') {
    return undefined;
  }

  let doc = cleanDocument(value);

  // Legado: CPF/CNPJ numérico podia perder zero à esquerda ao trafegar como number
  if (/^\d+$/.test(doc) && (doc.length === 10 || doc.length === 13)) {
    doc = `0${doc}`;
  }

  return doc || undefined;
}
